import React, { useState, useEffect } from "react";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  TrendingUp,
  Wallet,
  PiggyBank,
  CreditCard,
  Plus,
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencyFlags = {
  USD: "🇺🇸",
  KES: "🇰🇪",
  UGX: "🇺🇬",
  TZS: "🇹🇿",
  RWF: "🇷🇼",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [walletRes, txRes, statsRes] = await Promise.all([
          axios.get(`${API}/wallet`, { withCredentials: true }),
          axios.get(`${API}/transactions?limit=5`, { withCredentials: true }),
          axios.get(`${API}/transactions/stats`, { withCredentials: true }),
        ]);
        setWallet(walletRes.data);
        setTransactions(txRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error("Failed to fetch dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getTransactionIcon = (type) => {
    switch (type) {
      case "deposit":
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case "withdrawal":
      case "transfer":
      case "supplier_payment":
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      case "fx_conversion":
        return <ArrowLeftRight className="h-4 w-4 text-blue-600" />;
      default:
        return <ArrowLeftRight className="h-4 w-4 text-slate-600" />;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#14B8A6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(" ")[0]}!
            </h1>
            <p className="text-slate-600 mt-1">
              Here&apos;s what&apos;s happening with your account today.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/payments">
              <Button className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="quick-send-btn">
                <ArrowUpRight className="h-4 w-4 mr-2" />
                Send Money
              </Button>
            </Link>
            <Link to="/wallet">
              <Button variant="outline" className="rounded-full" data-testid="quick-deposit-btn">
                <Plus className="h-4 w-4 mr-2" />
                Deposit
              </Button>
            </Link>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Wallet Card - Large */}
          <Card className="md:col-span-5 overflow-hidden border-0 shadow-lg" data-testid="wallet-card">
            <div className="wallet-card-gradient p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm opacity-80">Total Balance</span>
                <Wallet className="h-5 w-5 opacity-80" />
              </div>
              <h2 className="text-4xl font-bold">
                ${wallet?.total_usd_value?.toLocaleString("en-US", { minimumFractionDigits: 2 }) || "0.00"}
              </h2>
              <p className="text-sm opacity-80 mt-1">USD equivalent</p>
              
              <div className="flex gap-3 mt-6">
                <Link to="/wallet">
                  <Button size="sm" className="rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm" data-testid="wallet-deposit-btn">
                    <Plus className="h-4 w-4 mr-2" /> Deposit
                  </Button>
                </Link>
                <Link to="/payments">
                  <Button size="sm" className="rounded-full bg-white hover:bg-slate-100 text-slate-900" data-testid="wallet-transfer-btn">
                    <ArrowUpRight className="h-4 w-4 mr-2" /> Transfer
                  </Button>
                </Link>
              </div>
            </div>

            {/* Currency Balances */}
            <CardContent className="p-4">
              <div className="space-y-2">
                {wallet?.balances?.filter(b => b.amount > 0).slice(0, 4).map((balance) => (
                  <div
                    key={balance.currency}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{currencyFlags[balance.currency]}</span>
                      <span className="font-medium text-slate-900">{balance.currency}</span>
                    </div>
                    <span className="font-semibold text-slate-900">
                      {balance.display_amount}
                    </span>
                  </div>
                ))}
                {(!wallet?.balances || wallet.balances.filter(b => b.amount > 0).length === 0) && (
                  <div className="text-center py-4 text-slate-500">
                    No balances yet. Make a deposit to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Transaction Chart */}
          <Card className="md:col-span-7 border-slate-200" data-testid="transactions-chart">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Transaction Activity</CardTitle>
              <Badge variant="secondary" className="font-normal">
                Last 30 days
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                {stats?.chart_data?.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.chart_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(val) => formatDate(val)}
                        tick={{ fontSize: 12, fill: "#64748B" }}
                        axisLine={{ stroke: "#E2E8F0" }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#64748B" }}
                        axisLine={{ stroke: "#E2E8F0" }}
                        tickFormatter={(val) => `$${val}`}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #E2E8F0",
                          boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                        }}
                        formatter={(value) => [`$${value.toFixed(2)}`, ""]}
                      />
                      <Line
                        type="monotone"
                        dataKey="total"
                        stroke="#14B8A6"
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 6, fill: "#14B8A6" }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-slate-500">
                    No transaction data yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Quick Stats */}
          <Card className="border-slate-200" data-testid="stats-sent">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Sent</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    ${stats?.summary?.total_sent?.toFixed(2) || "0.00"}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-red-50 flex items-center justify-center">
                  <ArrowUpRight className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stats-received">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Received</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    ${stats?.summary?.total_received?.toFixed(2) || "0.00"}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-green-50 flex items-center justify-center">
                  <ArrowDownLeft className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200" data-testid="stats-transactions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Transactions</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {stats?.summary?.transaction_count || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-[#14B8A6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions & Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Transactions */}
          <Card className="border-slate-200" data-testid="recent-transactions">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold">Recent Transactions</CardTitle>
              <Link to="/transactions">
                <Button variant="ghost" size="sm" className="text-[#14B8A6]" data-testid="view-all-tx-btn">
                  View All
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <div
                      key={tx.transaction_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                          {getTransactionIcon(tx.transaction_type)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">
                            {tx.recipient_name || tx.transaction_type.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          <p className="text-xs text-slate-500">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${
                          tx.transaction_type === "deposit" || tx.transaction_type === "credit_advance"
                            ? "text-green-600"
                            : "text-slate-900"
                        }`}>
                          {tx.transaction_type === "deposit" || tx.transaction_type === "credit_advance" ? "+" : "-"}
                          {tx.currency} {tx.amount.toLocaleString()}
                        </p>
                        <Badge
                          variant="secondary"
                          className={`text-xs ${
                            tx.status === "completed"
                              ? "bg-green-50 text-green-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {tx.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    No transactions yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-slate-200" data-testid="quick-actions">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/wallet">
                  <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group">
                    <div className="h-10 w-10 rounded-xl bg-[#14B8A6] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <ArrowLeftRight className="h-5 w-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-900">Convert</p>
                    <p className="text-xs text-slate-500">FX Conversion</p>
                  </div>
                </Link>
                <Link to="/vaults">
                  <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group">
                    <div className="h-10 w-10 rounded-xl bg-[#10B981] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <PiggyBank className="h-5 w-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-900">Save</p>
                    <p className="text-xs text-slate-500">Savings Vault</p>
                  </div>
                </Link>
                <Link to="/credit">
                  <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group">
                    <div className="h-10 w-10 rounded-xl bg-[#F59E0B] flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <CreditCard className="h-5 w-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-900">Borrow</p>
                    <p className="text-xs text-slate-500">Get Advance</p>
                  </div>
                </Link>
                <Link to="/recipients">
                  <div className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer group">
                    <div className="h-10 w-10 rounded-xl bg-purple-500 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                      <Plus className="h-5 w-5 text-white" />
                    </div>
                    <p className="font-medium text-slate-900">Add</p>
                    <p className="text-xs text-slate-500">New Recipient</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
